import React, { useState, useEffect, useCallback } from 'react';
import { Lock, LogOut, MapPin, CalendarDays, Users, Check, X, Car, Phone } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const DS_COLORS = { assigned: '#f5b301', accepted: '#16a34a', rejected: '#dc2626' };

const DriverPortal = () => {
  useSEO('Fahrerportal | CAB.TAXI', 'Driver portal', { noindex: true });
  const [token, setToken] = useState(() => localStorage.getItem('att_driver_token') || '');
  const [driver, setDriver] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { document.title = 'Driver Portal | CAB.TAXI'; }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('att_driver_token'); setToken(''); setDriver(null); setBookings([]);
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [me, bk] = await Promise.all([
        fetch(`${BACKEND}/api/driver/me`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${BACKEND}/api/driver/bookings`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (me.status === 401) { logout(); return; }
      setDriver(await me.json());
      setBookings(await bk.json());
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [token, logout]);

  useEffect(() => { load(); }, [load]);

  const login = async () => {
    setError('');
    try {
      const res = await fetch(`${BACKEND}/api/driver/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      localStorage.setItem('att_driver_token', data.token);
      setToken(data.token);
      setDriver(data.driver);
    } catch (e) { setError('Falsche E-Mail oder Passwort / Invalid credentials'); }
  };

  const respond = async (id, action) => {
    await fetch(`${BACKEND}/api/driver/bookings/${id}/respond`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action }),
    });
    load();
  };

  if (!token || !driver) {
    return (
      <div className="min-h-screen flex items-center justify-center brand-gradient px-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--brand-dark)' }}>
              <Car className="w-5 h-5" style={{ color: 'var(--brand-accent)' }} />
            </div>
            <div><div className="font-extrabold text-[color:var(--brand-dark)]">Driver Portal</div><div className="text-xs text-gray-400">CAB.TAXI</div></div>
          </div>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-Mail" data-testid="driver-email"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[color:var(--brand-dark)] mb-3" />
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && login()}
            placeholder="Passwort" data-testid="driver-password"
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[color:var(--brand-dark)]" />
          {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
          <button onClick={login} data-testid="driver-login-btn" className="accent-btn w-full rounded-xl py-3 font-bold mt-4">Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[color:var(--brand-muted)]">
      <header className="bg-[color:var(--brand-dark)] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-extrabold"><Car className="w-5 h-5" style={{ color: 'var(--brand-accent)' }} /> {driver.name}</div>
          <button onClick={logout} className="flex items-center gap-1.5 text-sm font-semibold bg-white/10 hover:bg-white/20 rounded-lg px-3 py-2"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </header>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-xl font-extrabold text-[color:var(--brand-dark)] mb-4">Meine Fahrten ({bookings.length})</h1>
        <div className="space-y-3" data-testid="driver-bookings">
          {bookings.length === 0 && <div className="bg-white rounded-2xl p-10 text-center text-gray-400">Keine Fahrten zugewiesen.</div>}
          {bookings.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl p-5 shadow-sm" data-testid={`driver-booking-${b.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="font-extrabold text-[color:var(--brand-dark)]">{b.reference}</span>
                <span className="text-xs font-bold text-white px-2 py-0.5 rounded-full capitalize" style={{ backgroundColor: DS_COLORS[b.driver_status] || '#888' }}>{b.driver_status}</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand-dark)]"><MapPin className="w-4 h-4" style={{ color: 'var(--brand-accent-hover)' }} /> {b.from_location} → {b.to_location || '—'}</div>
              <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {b.date} {b.time}</span>
                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {b.pax} pax</span>
                <span>{b.vehicleName}</span>
                <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {b.customer.code} {b.customer.phone}</span>
                <span className="font-bold text-[color:var(--brand-dark)]">{b.currency} {Math.round(b.price)}</span>
              </div>
              {b.driver_status !== 'accepted' && (
                <div className="mt-3 flex gap-2">
                  <button onClick={() => respond(b.id, 'accept')} data-testid={`driver-accept-${b.id}`} className="flex items-center gap-1.5 text-sm font-bold text-white rounded-lg px-4 py-2" style={{ backgroundColor: '#16a34a' }}><Check className="w-4 h-4" /> Annehmen</button>
                  <button onClick={() => respond(b.id, 'reject')} data-testid={`driver-reject-${b.id}`} className="flex items-center gap-1.5 text-sm font-bold text-red-600 border border-red-200 rounded-lg px-4 py-2 hover:bg-red-50"><X className="w-4 h-4" /> Ablehnen</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DriverPortal;
